<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div>
    <div class="field">
      <FieldLabel
        :label="$t('dashboardAdmin.blockEditor.markdownSource')"
        :help="$t('dashboardAdmin.blockEditor.help.markdownSource')"
      />
      <div class="source-options">
        <div class="radio-row">
          <PrimeRadioButton
            v-model="localSourceType"
            inputId="markdown_inline"
            value="inline"
          />
          <label for="markdown_inline" class="radio-label">
            {{ $t("dashboardAdmin.blockEditor.markdownSourceInline") }}
          </label>
        </div>
        <div class="radio-row">
          <PrimeRadioButton
            v-model="localSourceType"
            inputId="markdown_alfresco"
            value="alfresco"
          />
          <label for="markdown_alfresco" class="radio-label">
            {{ $t("dashboardAdmin.blockEditor.markdownSourceAlfresco") }}
          </label>
        </div>
      </div>
    </div>

    <!-- Alfresco Node ID -->
    <div v-if="localSourceType === 'alfresco'" class="field">
      <FieldLabel
        :label="$t('dashboardAdmin.blockEditor.markdownId')"
        :help="$t('dashboardAdmin.blockEditor.help.markdownId')"
        :example="$t('dashboardAdmin.blockEditor.examples.markdownId')"
      />
      <div class="input-wrapper" @click="$emit('open-markdown-browser')">
        <PrimeInputText
          :model-value="config.id"
          class="input-full clickable"
          readonly
          :placeholder="$t('dashboardAdmin.blockEditor.clickToSelectFile')"
        />
      </div>
    </div>

    <!-- Inline Markdown Content -->
    <div v-if="localSourceType === 'inline'" class="field">
      <FieldLabel
        :label="$t('dashboardAdmin.blockEditor.markdownContent')"
        :help="$t('dashboardAdmin.blockEditor.help.markdownContent')"
      />
      <PrimeTextarea
        :model-value="markdownContent"
        rows="8"
        class="input-full clickable"
        @click="$emit('open-markdown-editor')"
        readonly
        :placeholder="$t('dashboardAdmin.blockEditor.clickToEdit')"
      />
    </div>
  </div>
</template>

<script>
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import RadioButton from "primevue/radiobutton";
import FieldLabel from "@/components/dashboard/editor/FieldLabel.vue";

export default {
  name: "MarkdownConfig",
  components: {
    PrimeInputText: InputText,
    PrimeTextarea: Textarea,
    PrimeRadioButton: RadioButton,
    FieldLabel,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
  },
  emits: ["update:config", "open-markdown-editor", "open-markdown-browser"],
  data() {
    return {
      localSourceType: "inline",
      userChangedType: false,
    };
  },
  computed: {
    markdownContent() {
      return this.config?.markdown || "";
    },
  },
  watch: {
    config: {
      immediate: true,
      handler() {
        // Only auto-detect on first load, not when user manually changes type
        if (!this.userChangedType) {
          this.detectMarkdownSourceType();
        }
      },
    },
    localSourceType(newType) {
      this.userChangedType = true;
      const newConfig = { ...this.config };

      // Clear the opposite field when switching modes
      if (newType === "inline") {
        newConfig.id = "";
      } else if (newType === "alfresco") {
        newConfig.markdown = "";
      }

      this.$emit("update:config", newConfig);
    },
  },
  methods: {
    detectMarkdownSourceType() {
      // If there's an ID, it's loading from Alfresco
      if (this.config?.id) {
        this.localSourceType = "alfresco";
      } else {
        this.localSourceType = "inline";
      }
    },
  },
};
</script>

<style scoped>
.field {
  margin-bottom: 1rem;
}

.source-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.radio-row {
  display: flex;
  align-items: center;
}

.radio-label {
  margin-left: 0.5rem;
  cursor: pointer;
}

.clickable {
  cursor: pointer;
}

.input-full {
  width: 100%;
}

.input-wrapper {
  cursor: pointer;
}
</style>
